/**
 * PDF Export Button Component
 * Dropdown showing existing PDFs and option to generate new
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { authService } from '../services/auth-service';
import { Spinner, Button } from '@/components/ui';
import { logger } from '../utils/logger';

interface PDFInfo {
  key: string;
  filename: string;
  timestamp: string;
  size: number;
  url: string;
}

interface PDFExportButtonProps {
  projectId: string;
  documentNumber: string;
  documentTitle: string;
  revision: string;
  disabled?: boolean;
  className?: string;
  /** Callback to open the generated PDF in the app's document viewer */
  onOpenPdfInViewer?: (pdfUrl: string, title: string) => void;
}

// Default options for a nicely formatted aerospace document PDF
const DEFAULT_PDF_OPTIONS = {
  pageSize: 'letter' as const,
  includeHeaderFooter: true,
  includeCoverPage: true,
  includeTOC: true,
  includeRevisionHistory: true,
  pageOrientation: 'portrait' as const,
};

// Format file size
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Format timestamp for display
function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  
  if (diffHours < 1) {
    const diffMins = Math.floor(diffMs / (1000 * 60));
    return diffMins <= 1 ? 'Just now' : `${diffMins} min ago`;
  }
  if (diffHours < 24) {
    return `${Math.floor(diffHours)}h ago`;
  }
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

export default function PDFExportButton({
  projectId,
  documentNumber,
  documentTitle,
  revision,
  disabled = false,
  className = '',
  onOpenPdfInViewer
}: PDFExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [existingPdfs, setExistingPdfs] = useState<PDFInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch existing PDFs when dropdown opens
  useEffect(() => {
    if (isOpen && existingPdfs.length === 0) {
      fetchExistingPdfs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only fetch on modal open, not on every pdf list change
  }, [isOpen]);

  const fetchExistingPdfs = async () => {
    setIsLoading(true);
    try {
      const tokens = await authService.getTokens();
      const baseURL = import.meta.env?.VITE_API_URL || '/api';
      
      const response = await axios.get(
        `${baseURL}/v1/projects/${projectId}/documents/${documentNumber}/pdfs`,
        {
          headers: { 'Authorization': `Bearer ${tokens?.idToken}` }
        }
      );
      
      setExistingPdfs(response.data?.data?.pdfs || response.data?.pdfs || []);
    } catch (err: unknown) {
      logger.error('Failed to fetch existing PDFs:', err);
      // Don't show error - just show empty list
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPdf = (pdf: PDFInfo) => {
    const pdfTitle = `${documentNumber} Rev ${revision}`;
    if (onOpenPdfInViewer) {
      onOpenPdfInViewer(pdf.url, pdfTitle);
    } else {
      window.open(pdf.url, '_blank');
    }
    setIsOpen(false);
  };

  const handleGenerateNew = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const tokens = await authService.getTokens();
      const baseURL = import.meta.env?.VITE_API_URL || '/api';
      
      const options = {
        ...DEFAULT_PDF_OPTIONS,
        watermark: revision === 'Draft' ? 'draft' : 'none',
        documentNumber,
        documentTitle,
        revision
      };
      
      const response = await axios.post(
        `${baseURL}/v1/projects/${projectId}/documents/${documentNumber}/generate-pdf`,
        options,
        {
          headers: {
            'Authorization': `Bearer ${tokens?.idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const pdfData = response.data?.data || response.data;
      
      if (pdfData && pdfData.pdfUrl) {
        const pdfTitle = `${documentNumber} Rev ${revision}`;
        
        if (onOpenPdfInViewer) {
          onOpenPdfInViewer(pdfData.pdfUrl, pdfTitle);
        } else {
          const link = document.createElement('a');
          link.href = pdfData.pdfUrl;
          link.download = `${documentNumber}-${revision}.pdf`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
        // Refresh the list
        fetchExistingPdfs();
      } else {
        throw new Error('PDF generation failed - no download URL received');
      }
    } catch (err: unknown) {
      logger.error('PDF export error:', err);
      setError(err.message || 'Failed to generate PDF');
    } finally {
      setIsGenerating(false);
      setIsOpen(false);
    }
  };

  // Base classes for flex layout (always applied)
  const baseClasses = "inline-flex items-center gap-1";
  const _defaultClasses = "py-2 px-4 bg-white bg-opacity-20 text-white border border-white border-opacity-30 rounded cursor-pointer text-sm font-semibold hover:bg-white hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Main button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isGenerating}
        variant="secondary"
        className={`${baseClasses} ${className || ''}`}
        title="PDF options"
      >
        {isGenerating ? (
          <>
            <Spinner size="sm" />
            <span>Generating...</span>
          </>
        ) : (
          <>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
            </svg>
            <span>PDF</span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
            </svg>
          </>
        )}
      </Button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-1 w-64 bg-card rounded-lg shadow-lg border border-border z-50 overflow-hidden">
          {/* Header */}
          <div className="px-3 py-2 bg-muted border-b border-border">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              PDF Exports
            </span>
          </div>

          {/* Existing PDFs list */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                <Spinner className="mx-auto mb-1" />
                Loading...
              </div>
            ) : existingPdfs.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                No existing PDFs
              </div>
            ) : (
              existingPdfs.map((pdf, index) => (
                <Button
                  key={pdf.key}
                  onClick={() => handleOpenPdf(pdf)}
                  variant="ghost"
                  className={`w-full px-3 py-2 justify-start h-auto rounded-none flex items-center gap-2 ${
                    index === 0 ? 'bg-info-light' : ''
                  }`}
                >
                  <svg className="w-4 h-4 text-destructive flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
                  </svg>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm text-foreground truncate flex items-center gap-1">
                      {formatTimestamp(pdf.timestamp)}
                      {index === 0 && (
                        <span className="text-xs bg-info-light text-info px-1 rounded">
                          Latest
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatSize(pdf.size)}
                    </div>
                  </div>
                  <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                </Button>
              ))
            )}
          </div>

          {/* Generate new button */}
          <div className="border-t border-border">
            <Button
              onClick={handleGenerateNew}
              disabled={isGenerating}
              variant="ghost"
              className="w-full px-3 py-2 justify-start h-auto rounded-none flex items-center gap-2 text-info font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
              </svg>
              <span className="text-sm">Generate New PDF</span>
            </Button>
          </div>
        </div>
      )}

      {/* Error toast */}
      {error && (
        <div className="fixed bottom-4 right-4 p-4 bg-destructive-light border border-destructive rounded-lg shadow-lg text-sm text-destructive z-[1001] max-w-md">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
            </svg>
            <div>
              <div className="font-semibold">PDF Export Failed</div>
              <div>{error}</div>
            </div>
            <Button
              onClick={() => setError(null)}
              variant="ghost"
              size="icon"
              className="ml-auto text-destructive hover:text-destructive h-6 w-6"
            >
              ×
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
